import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api, { setToken, clearToken, getToken } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Clock, LogOut } from "lucide-react";

const AuthContext = createContext(null);

const IDLE_LIMIT_MS = 30 * 60 * 1000; // 30 min
const WARN_5_MS = 25 * 60 * 1000;     // 5 min before logout
const WARN_2_MS = 28 * 60 * 1000;     // 2 min before logout

function fmt(secs) {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = authed
  const [ready, setReady] = useState(false);
  const [idleWarn, setIdleWarn] = useState(null); // null | 5 | 2
  const [secsLeft, setSecsLeft] = useState(0);
  const lastActivity = useRef(Date.now());

  const refresh = useCallback(async () => {
    if (!getToken()) { setUser(false); setReady(true); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      clearToken(); setUser(false);
    } finally { setReady(true); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const completeLogin = (token, userObj) => {
    setToken(token);
    lastActivity.current = Date.now();
    setUser(userObj);
  };

  const logout = useCallback(() => {
    clearToken();
    setUser(false);
    window.location.href = "/login";
  }, []);

  const stayActive = useCallback(() => {
    lastActivity.current = Date.now();
    setIdleWarn(null);
  }, []);

  // inactivity auto-logout
  useEffect(() => {
    if (!user) return;
    lastActivity.current = Date.now();
    const bump = () => { lastActivity.current = Date.now(); };
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "wheel"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));

    const iv = setInterval(() => {
      const idle = Date.now() - lastActivity.current;
      if (idle >= IDLE_LIMIT_MS) { logout(); return; }
      setSecsLeft(Math.max(0, Math.ceil((IDLE_LIMIT_MS - idle) / 1000)));
      if (idle >= WARN_2_MS) setIdleWarn(2);
      else if (idle >= WARN_5_MS) setIdleWarn(5);
      else setIdleWarn(null);
    }, 1000);

    return () => { events.forEach((e) => window.removeEventListener(e, bump)); clearInterval(iv); };
  }, [user, logout]);

  return (
    <AuthContext.Provider value={{ user, ready, refresh, completeLogin, logout, isAdmin: user && user.role === "admin" }}>
      {children}

      <Dialog open={!!idleWarn} onOpenChange={() => {}}>
        <DialogContent className="bg-[#0B0C10] border-[#EAE04A]/40 text-white" data-testid="idle-warning-dialog"
          onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-mono-jb uppercase tracking-wider text-[#EAE04A] flex items-center gap-2">
              <Clock size={16} /> Session about to expire
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-4">
            <p className="text-sm text-[#8B949E] leading-relaxed">
              You've been inactive. For security you'll be signed out in{" "}
              <span className="text-white font-bold" data-testid="idle-countdown">{fmt(secsLeft)}</span>
              {idleWarn === 2 ? " — that's under 2 minutes." : "."}
            </p>
            <div className="flex gap-3">
              <Button data-testid="stay-signed-in" onClick={stayActive}
                className="rounded-sm bg-[#00F0FF] text-black font-mono-jb uppercase text-xs tracking-widest font-bold hover:bg-[#00F0FF]">
                Stay signed in
              </Button>
              <Button data-testid="logout-now" onClick={logout} variant="outline"
                className="rounded-sm border-[#FF3B30]/30 bg-transparent text-[#FF3B30] hover:bg-[#FF3B30]/10 hover:text-[#FF3B30] font-mono-jb uppercase text-xs tracking-widest">
                <LogOut size={13} className="mr-1" /> Log out now
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
